'use client';

/**
 * Geoapify Context Provider for address autocomplete and geocoding.
 *
 * All requests are routed through the backend ``/geoapify`` pass-through proxy
 * (issues #8781 / #8782) so the server-side ``GEOAPIFY_API_KEY`` is never
 * exposed to the browser and Geoapify never receives the visitor's IP or
 * search query directly. The proxy injects the key and enforces a per-IP
 * rate limit.
 *
 * Provides:
 * - Geocoding utilities (forward + reverse)
 * - Address autocomplete functionality
 *
 * Issue: #1122 Professional Location & Service Areas with Geoapify Integration
 */

import { HOST } from '@/lib/config';
import type {
  GeoapifyAutocompleteResult,
  GeoapifyGeocodingResult,
  GeoapifyResponse,
} from '@/types/regions-types';
import { type ReactNode, createContext, useCallback, useMemo } from 'react';

// =============================================================================
// Constants
// =============================================================================

/** Backend pass-through proxy base (replaces direct https://api.geoapify.com/v1). */
const GEOAPIFY_PROXY_BASE = `${HOST}/geoapify/geocode`;
const DEFAULT_LANG = 'sk';
const DEFAULT_COUNTRY_FILTER = 'sk';

// =============================================================================
// Types
// =============================================================================

interface GeoapifyContextType {
  /** Whether Geoapify is configured with an API key */
  isConfigured: boolean;

  /**
   * Forward geocode an address to coordinates.
   * @param address - Free-form address string
   * @param options - Optional geocoding options
   * @returns Geocoding result or null
   */
  geocode: (
    address: string,
    options?: GeocodeOptions
  ) => Promise<GeoapifyGeocodingResult | null>;

  /**
   * Reverse geocode coordinates to an address.
   * @param lat - Latitude
   * @param lon - Longitude
   * @param lang - Language for results (default: sk)
   * @returns Geocoding result or null
   */
  reverseGeocode: (
    lat: number,
    lon: number,
    lang?: string
  ) => Promise<GeoapifyGeocodingResult | null>;

  /**
   * Get address autocomplete suggestions.
   * @param text - Partial address text
   * @param options - Optional autocomplete options
   * @returns List of suggestions
   */
  autocomplete: (
    text: string,
    options?: AutocompleteOptions
  ) => Promise<GeoapifyAutocompleteResult[]>;

  /**
   * Get the browser's current location.
   * @returns Coordinates or null if denied/unavailable
   */
  getCurrentLocation: () => Promise<{ lat: number; lon: number } | null>;
}

interface GeocodeOptions {
  /** Filter by country code(s) */
  countryCode?: string;
  /** Language for results */
  lang?: string;
  /** Max results */
  limit?: number;
}

interface AutocompleteOptions {
  /** Filter by country code(s), comma-separated */
  countryCodes?: string[];
  /** Language for results */
  lang?: string;
  /** Max suggestions */
  limit?: number;
  /** Filter by types (street, city, postcode, etc.) */
  type?: string;
}

// =============================================================================
// Context
// =============================================================================

const GeoapifyContext = createContext<GeoapifyContextType | undefined>(
  undefined
);

// =============================================================================
// Provider
// =============================================================================

interface GeoapifyProviderProps {
  children: ReactNode;
}

export function GeoapifyProvider({ children }: GeoapifyProviderProps) {
  // The API key now lives server-side (backend /geoapify proxy). The proxy is
  // always wired once the backend is deployed, so this stays true; on the
  // off chance Geoapify isn't configured server-side the proxy returns 503
  // and each call degrades to null/[] as before.
  const isConfigured = true;

  /**
   * Forward geocode an address to coordinates.
   */
  const geocode = useCallback(
    async (
      address: string,
      options: GeocodeOptions = {}
    ): Promise<GeoapifyGeocodingResult | null> => {
      const params = new URLSearchParams({
        text: address,
        lang: options.lang || DEFAULT_LANG,
        limit: String(options.limit || 1),
      });

      if (options.countryCode) {
        params.append(
          'filter',
          `countrycode:${options.countryCode.toLowerCase()}`
        );
      }

      try {
        const response = await fetch(
          `${GEOAPIFY_PROXY_BASE}/search?${params.toString()}`
        );

        if (!response.ok) {
          console.error('Geoapify geocode error:', response.status);
          return null;
        }

        const data: GeoapifyResponse<GeoapifyGeocodingResult> =
          await response.json();

        return data.results?.[0] || null;
      } catch (error) {
        console.error('Geoapify geocode failed:', error);
        return null;
      }
    },
    []
  );

  /**
   * Reverse geocode coordinates to an address.
   */
  const reverseGeocode = useCallback(
    async (
      lat: number,
      lon: number,
      lang: string = DEFAULT_LANG
    ): Promise<GeoapifyGeocodingResult | null> => {
      const params = new URLSearchParams({
        lat: String(lat),
        lon: String(lon),
        lang,
      });

      try {
        const response = await fetch(
          `${GEOAPIFY_PROXY_BASE}/reverse?${params.toString()}`
        );

        if (!response.ok) {
          console.error('Geoapify reverse geocode error:', response.status);
          return null;
        }

        const data: GeoapifyResponse<GeoapifyGeocodingResult> =
          await response.json();

        return data.results?.[0] || null;
      } catch (error) {
        console.error('Geoapify reverse geocode failed:', error);
        return null;
      }
    },
    []
  );

  /**
   * Get address autocomplete suggestions.
   */
  const autocomplete = useCallback(
    async (
      text: string,
      options: AutocompleteOptions = {}
    ): Promise<GeoapifyAutocompleteResult[]> => {
      // Require minimum 2 characters
      if (text.length < 2) {
        return [];
      }

      const params = new URLSearchParams({
        text,
        lang: options.lang || DEFAULT_LANG,
        limit: String(options.limit || 5),
      });

      // Apply country filter
      const countries = options.countryCodes?.length
        ? options.countryCodes.map((c) => c.toLowerCase()).join(',')
        : DEFAULT_COUNTRY_FILTER;
      params.append('filter', `countrycode:${countries}`);

      if (options.type) {
        params.append('type', options.type);
      }

      try {
        const response = await fetch(
          `${GEOAPIFY_PROXY_BASE}/autocomplete?${params.toString()}`
        );

        if (!response.ok) {
          console.error('Geoapify autocomplete error:', response.status);
          return [];
        }

        const data: GeoapifyResponse<GeoapifyGeocodingResult> =
          await response.json();

        // Map to autocomplete result format
        return (data.results || []).map((r) => ({
          formatted: r.formatted,
          lat: r.lat,
          lon: r.lon,
          country_code: r.country_code?.toUpperCase() || null,
          city: r.city,
          postcode: r.postcode,
          street: r.street,
          housenumber: r.housenumber,
          result_type: r.result_type,
          place_id: r.place_id,
        }));
      } catch (error) {
        console.error('Geoapify autocomplete failed:', error);
        return [];
      }
    },
    []
  );

  /**
   * Get the browser's current location using Geolocation API.
   */
  const getCurrentLocation = useCallback(async (): Promise<{
    lat: number;
    lon: number;
  } | null> => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      console.warn('Geolocation not available');
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Geolocation error:', error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes cache
        }
      );
    });
  }, []);

  const value = useMemo<GeoapifyContextType>(
    () => ({
      isConfigured,
      geocode,
      reverseGeocode,
      autocomplete,
      getCurrentLocation,
    }),
    [isConfigured, geocode, reverseGeocode, autocomplete, getCurrentLocation]
  );

  return (
    <GeoapifyContext.Provider value={value}>
      {children}
    </GeoapifyContext.Provider>
  );
}
